/**
 * ChatMessageContainer - Single UI Descriptor Renderer
 * 
 * Renders a single UI descriptor by resolving the component from the catalog
 * and passing through the appropriate props.
 */

import PropTypes from 'prop-types';
import { memo, useCallback, useMemo } from 'react';
import { resolve } from '../chat-components';

const ChatMessageContainer = memo(({ descriptor, onAction }) => {
    const { id, component, type, data, children, isStreaming, isComplete, error } = descriptor;

    // Use 'component' field, fallback to 'type' for compatibility
    const componentType = component || type;

    // Resolve component from catalog
    // Pass data as a prop (it can be a string or object)
    // Also pass streaming/complete state flags
    const { Component, props: mergedProps, found } = useMemo(() => {
        const baseProps = { id, onAction, isStreaming, isComplete, error };

        const propsToPass = typeof data === 'object' && data !== null
            ? { ...data, ...baseProps }
            : { data, ...baseProps };

        return resolve(componentType, propsToPass);
    }, [componentType, data, id, onAction, isStreaming, isComplete, error]);

    // Handle action callback with descriptor context
    const handleAction = useCallback((actionType, payload) => {
        onAction?.(actionType, {
            ...payload,
            descriptorId: id,
            componentType
        });
    }, [onAction, id, componentType]);

    // Render children recursively if present
    const renderedChildren = useMemo(() => {
        if (!children || children.length === 0) return null;

        return children.map((child) => (
            <ChatMessageContainer
                key={child.id}
                descriptor={child}
                onAction={onAction}
            />
        ));
    }, [children, onAction]);

    // Log warning for unknown components in development
    if (!found && process.env.NODE_ENV === 'development') {
        console.warn(`ChatMessageContainer: Unknown component type "${componentType}"`, descriptor);
    }

    return (
        <Component
            {...mergedProps}
            onAction={handleAction}
        >
            {renderedChildren}
        </Component>
    );
});

ChatMessageContainer.displayName = 'ChatMessageContainer';

ChatMessageContainer.propTypes = {
    descriptor: PropTypes.shape({
        id: PropTypes.string.isRequired,
        component: PropTypes.string,
        type: PropTypes.string,
        data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        children: PropTypes.array,
        isStreaming: PropTypes.bool,
        isComplete: PropTypes.bool,
        error: PropTypes.bool
    }).isRequired,
    onAction: PropTypes.func
};

export default ChatMessageContainer;
